"use client";

import { useEffect } from "react";
import { fetchAuthSession } from "aws-amplify/auth";
import { Hub } from "aws-amplify/utils";

// Step 8a commit 3: mirrors the Cognito access token into a
// browser-readable cookie (mag-portal-access) on every session change
// so middleware.ts (which only sees cookies, not localStorage) can
// fast-gate /admin/* routes. The cookie is NOT httpOnly because the
// browser SDK writes it - JS-set cookies cannot be httpOnly. The
// security model is unchanged: the same token is already in
// localStorage via Amplify, and the cryptographic check happens in
// requireAdmin / verifyAccessToken on every API request.

const COOKIE_NAME = "mag-portal-access";

function setCookie(value: string, maxAgeSeconds: number) {
  if (typeof document === "undefined") return;
  const parts = [
    `${COOKIE_NAME}=${value}`,
    "path=/",
    `max-age=${maxAgeSeconds}`,
    "samesite=lax",
  ];
  if (window.location.protocol === "https:") parts.push("secure");
  document.cookie = parts.join("; ");
}

function clearCookie() {
  setCookie("", 0);
}

// Exported so the sign-in page can await the cookie write BEFORE
// redirecting to /dashboard. Without that, router.push can race the
// Hub "signedIn" listener: middleware sees no cookie yet and bounces
// the fresh session back to /sign-in?from=/dashboard.
export async function syncCookieFromSession(): Promise<void> {
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.accessToken?.toString();
    if (!token) {
      clearCookie();
      return;
    }
    // Cognito access tokens expire in 1h. The cookie matches so a
    // stale cookie never out-lives the underlying token.
    setCookie(token, 3600);
  } catch {
    clearCookie();
  }
}

// PASTE #282: record a real client sign-in once per browser session.
// Fires only on genuine new sessions (signedIn / signInWithRedirect, NOT
// tokenRefresh), and impersonation never emits those Hub events, so this
// only ever records real client logins. Best-effort + guarded by a
// sessionStorage flag so a re-mount / navigation can't double-count.
const RECORD_FLAG = "mag-signin-recorded";
// #64 F3 (Jul 14): the `signedIn` Hub event fires for BOTH password and
// magic-link sign-ins, so the listener cannot tell them apart and used to
// label every one "portal". The magic-link redeem path sets this hint right
// before it completes, so an email sign-in is recorded as "magic-link" (the
// primary launch method - Alexis watches the method column during the blast).
export const SIGNIN_METHOD_HINT = "mag-signin-method";

// Pure so it is unit-testable: a valid hint wins, else the Hub-event default.
// Server allow-list (ALLOWED_METHODS in the record route) also gates the value.
export function resolveSignInMethod(
  hint: string | null,
  defaultMethod: "portal" | "google",
): "portal" | "google" | "magic-link" | "password" {
  return hint === "magic-link" || hint === "password" ? hint : defaultMethod;
}

async function recordSignIn(
  defaultMethod: "portal" | "google",
): Promise<void> {
  try {
    if (typeof window === "undefined") return;
    if (window.sessionStorage.getItem(RECORD_FLAG)) return;
    const method = resolveSignInMethod(
      window.sessionStorage.getItem(SIGNIN_METHOD_HINT),
      defaultMethod,
    );
    window.sessionStorage.removeItem(SIGNIN_METHOD_HINT);
    const session = await fetchAuthSession();
    const token = session.tokens?.accessToken?.toString();
    if (!token) return;
    window.sessionStorage.setItem(RECORD_FLAG, "1");
    await fetch("/api/auth/session/record", {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ method }),
      keepalive: true,
    });
  } catch {
    // telemetry only - never surface
  }
}

// #513 Part A: presence heartbeat. While the tab is focused/visible and there
// is a live session, POST /api/auth/session/ping every few minutes so the admin
// activity board's "Active now" reflects real presence (not just the discrete
// sign-in moment). Best-effort + off the critical path; errors are swallowed.
const HEARTBEAT_MS = 4 * 60 * 1000;

async function sendPing(): Promise<void> {
  try {
    if (typeof document === "undefined") return;
    if (document.visibilityState !== "visible") return;
    const session = await fetchAuthSession();
    const token = session.tokens?.accessToken?.toString();
    if (!token) return;
    await fetch("/api/auth/session/ping", {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      keepalive: true,
    });
  } catch {
    // presence telemetry only - never surface
  }
}

export function AccessTokenCookieMirror() {
  useEffect(() => {
    syncCookieFromSession();
    // Presence heartbeat: fire once on mount (covers a silent session-restore
    // that emits no Hub `signedIn` event), then on an interval + tab-focus.
    void sendPing();
    const heartbeat = setInterval(() => void sendPing(), HEARTBEAT_MS);
    const onVisible = () => {
      if (typeof document !== "undefined" && document.visibilityState === "visible") {
        void sendPing();
      }
    };
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", onVisible);
    }
    const unsubscribe = Hub.listen("auth", ({ payload }) => {
      if (
        payload.event === "signedIn" ||
        payload.event === "signInWithRedirect" ||
        payload.event === "tokenRefresh"
      ) {
        syncCookieFromSession();
        if (payload.event === "signedIn") void recordSignIn("portal");
        else if (payload.event === "signInWithRedirect") void recordSignIn("google");
      } else if (
        payload.event === "signedOut" ||
        payload.event === "tokenRefresh_failure"
      ) {
        clearCookie();
        if (typeof window !== "undefined") {
          window.sessionStorage.removeItem(RECORD_FLAG);
        }
      }
    });
    return () => {
      unsubscribe();
      clearInterval(heartbeat);
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVisible);
      }
    };
  }, []);
  return null;
}
